/**
 * O3 (Salon Owner localization & polish) — owner-portal strings for the
 * "shell" area (portal shell, Dashboard, Calendar page + Quick book, Appointments,
 * Help). Merged into the runtime dictionary by i18n/owner/index.ts and read
 * through LanguageContext's t(). Every key needs BOTH an en and an ar value.
 */
import type { OwnerStrings } from './types';

export const shellStrings: OwnerStrings = {
    en: {
        // ── Shell: account menu + sidebar ────────────────────────────────
        'shell.menu.profileMenu': 'Profile menu',
        'shell.menu.profileOptions': 'Profile options',
        'shell.menu.userFallback': 'User',
        'shell.menu.setUp': 'SET UP',
        'shell.menu.loggingOut': 'Logging out…',
        'shell.menu.logoutFailed': 'Logout failed. Please try again.',
        'shell.sidebar.collapse': 'Collapse sidebar',
        'shell.sidebar.expand': 'Expand sidebar',

        // ── Dashboard ────────────────────────────────────────────────────
        'dash.export': 'Export',
        'dash.bookingsRevenue.title': 'Bookings & Revenue',
        'dash.appointmentStats.title': 'Appointment Statistics',
        'dash.revenue.title': 'Revenue statistics',
        'dash.revenue.series': 'Revenue',
        'dash.payments.recentTitle': 'Recent payments',
        'dash.payments.recentEmpty': 'No recent payments',
        'dash.payments.appointmentRef': 'Appointment #{id}',
        'dash.payments.dateVia': '{date} via {method}',
        'dash.payments.byMethodTitle': 'Revenue by payment method',
        'dash.payments.byMethodEmpty': 'No payment data yet',
        'dash.payments.txnCount': '{count} payments',
        'dash.payments.status.paid': 'Paid',
        'dash.payments.status.pending': 'Pending',
        'dash.payments.status.failed': 'Failed',
        'dash.payments.status.refunded': 'Refunded',
        'dash.payments.method.cash': 'Cash',
        'dash.payments.method.card': 'Card',
        'dash.payments.method.online': 'Online',

        // ── Calendar page ────────────────────────────────────────────────
        'cal.title': 'Calendar',
        'cal.viewMode': 'Calendar view',
        'cal.view.day': 'Day',
        'cal.view.week': 'Week',
        'cal.prevDay': 'Previous day',
        'cal.nextDay': 'Next day',
        'cal.prevWeek': 'Previous week',
        'cal.nextWeek': 'Next week',
        'cal.today': 'Today',
        'cal.pickDate': 'Pick a date',
        'cal.selectBranch': 'Select branch',
        'cal.hq': 'HQ',
        'cal.quickBook': '+ Quick book',
        'cal.quickBookWith': 'Quick book with {name} +',
        'cal.quickBookNeedsBranch': 'Pick a branch first.',
        'cal.loadFailed': 'Couldn’t load the calendar.',
        'cal.retry': 'Retry',
        'cal.closed.title': 'Closed on this day',
        'cal.closed.body': 'This branch is closed on {date}. You can change opening hours in My Business › Business details › Business hours.',
        'cal.toast.nobodyOnShift': 'Nobody is on shift around {time} on {date}. You can still pick another time or staff member.',
        'cal.staff.openActions': 'Actions for {name}',
        'cal.staff.actions': 'Actions for {name}',
        'cal.staff.female': 'Female staff',
        'cal.staff.male': 'Male staff',
        'cal.staff.off': 'Off',
        'cal.staff.clickToBook': 'Click an empty slot to book {name}',

        // ── Quick book modal ─────────────────────────────────────────────
        'cal.qb.dialog': 'Quick book an appointment',
        'cal.qb.title.quickBook': 'Quick book',
        'cal.qb.title.bookWith': 'Book with {name}',
        'cal.qb.title.bookAt': 'Book {name} at {time}',
        'cal.qb.title.pickTime': 'Pick a time for {service}',
        'cal.qb.atBranch': 'At {branch}',
        'cal.qb.close': 'Close',
        'cal.qb.phase.staff': 'Staff',
        'cal.qb.phase.service': 'Service',
        'cal.qb.phase.slot': 'Time',
        'cal.qb.phase.confirm': 'Confirm',
        'cal.qb.stepOf': 'Step {n} of {total} — {label}',
        'cal.qb.step.category': 'Pick a category',
        'cal.qb.step.service': 'Pick a service',
        'cal.qb.step.slot': 'Pick a time for {service}',
        'cal.qb.step.confirm': 'Confirm booking',
        'cal.qb.loadingCategories': 'Loading categories…',
        'cal.qb.loadingServices': 'Loading services…',
        'cal.qb.noCategories': 'No active categories yet. Add one in My Business › Categories first.',
        'cal.qb.noServices': 'No active services in this category. Add one in My Business › Services.',
        'cal.qb.staffNoServices': '{name} doesn’t offer any services in this category.',
        'cal.qb.thisStaff': 'This staff member',
        'cal.qb.service': 'Service',
        'cal.qb.serviceTypeLabel': 'For',
        'cal.qb.serviceType.men': 'Men only',
        'cal.qb.serviceType.women': 'Women only',
        'cal.qb.serviceType.everyone': 'Everyone',
        'cal.qb.when': 'When',
        'cal.qb.tomorrow': 'Tomorrow',
        'cal.qb.pickDate': 'Pick a specific date',
        'cal.qb.time': 'Time',
        'cal.qb.any': 'Any',
        'cal.qb.morning': 'Morning',
        'cal.qb.afternoon': 'Afternoon',
        'cal.qb.evening': 'Evening',
        'cal.qb.specific': 'Specific',
        'cal.qb.specificTime': 'Specific time',
        'cal.qb.staff': 'Staff',
        'cal.qb.female': 'Female',
        'cal.qb.male': 'Male',
        'cal.qb.bothGenders': 'The salon serves both men and women',
        'cal.qb.scope.staff': '{name}’s free times {when} on {date}, within their shift and the service length',
        'cal.qb.scope.all': 'Free times {when} on {date}, within branch hours, staff shifts and existing bookings',
        'cal.qb.scope.around': 'around {time}',
        'cal.qb.scope.anyTime': 'at any time',
        'cal.qb.slotsFailed': 'Couldn’t load available times. Please try again.',
        'cal.qb.findingSlots': 'Finding available times…',
        'cal.qb.noSlotsStaff': 'No free times for {name} on this day. Try a different date.',
        'cal.qb.noSlots': 'No available times match your filters. Try a different date.',
        'cal.qb.closestMatch': 'No exact match for {time}. Showing the closest times.',
        'cal.qb.availableSlots': 'Available times',
        'cal.qb.match': 'Closest',
        'cal.qb.duration': 'Duration',
        'cal.qb.unit.min': 'min',
        'cal.qb.unit.hr': 'h',
        'cal.qb.with': 'With',
        'cal.qb.date': 'Date',
        'cal.qb.customerName': 'Customer name (optional)',
        'cal.qb.walkIn': 'Walk-in',
        'cal.qb.phone': 'Phone (optional)',
        'cal.qb.cancel': 'Cancel',
        'cal.qb.back': 'Back',
        'cal.qb.continue': 'Continue to confirm',
        'cal.qb.book': 'Book',
        'cal.qb.booking': 'Booking…',
        'cal.qb.toast.confirmed': 'Booking confirmed.',
        'cal.qb.toast.exists': 'This booking already exists.',
        'cal.qb.toast.failed': 'Booking failed. Please try again.',

        // ── Appointments list ────────────────────────────────────────────
        'appt.tabsAria': 'Appointment views',
        'appt.durationMinutes': '{n} min',

        // ── Help ─────────────────────────────────────────────────────────
        'help.o.faqTitle': 'Frequently asked questions',
        'help.o.needMoreTitle': 'Need more help?',
        // Ticket-mode copy (dormant while tickets are off) — kept word-for-word as signed off (AB#2133).
        'help.o.needMoreTickets': 'If you cannot find the answer you are looking for, please visit the Support page to submit a ticket. Our team is available to assist you.',
        'help.o.faq.branch.q': 'How do I add a new branch?',
        'help.o.faq.branch.a': 'Open My Business › Branches and choose “Add branch”. Fill in the name, address and phone number.',
        'help.o.faq.appointments.q': 'How do I manage my appointments?',
        'help.o.faq.appointments.a': 'Open Appointments or Calendar from the sidebar. Open a booking to confirm, decline, mark it as paid or see it on the calendar.',
        'help.o.faq.hours.q': 'How do I update my business hours?',
        'help.o.faq.hours.a': 'Open My Business › Business details and go to Business hours. Set the weekly hours, then choose which branches the change applies to.',
        'help.o.faq.services.q': 'How do I add services?',
        'help.o.faq.services.a': 'Open My Business › Services and choose “Add service”. Enter the name, duration, price and category.',
        'help.o.faq.contact.q': 'How do I contact support?',
        'help.o.faq.contact.a': 'Go to the Support page from the sidebar and click "New Ticket" to submit a support request. Our team will respond within 24 hours.',
        'help.o.faq.password.q': 'How do I change my password?',
        'help.o.faq.password.a': 'Open your profile menu, choose Account settings and then “Change password”. Enter your current password, then the new one.',
    },
    ar: {
        // ── Shell: account menu + sidebar ────────────────────────────────
        'shell.menu.profileMenu': 'قائمة الحساب',
        'shell.menu.profileOptions': 'خيارات الحساب',
        'shell.menu.userFallback': 'المستخدم',
        'shell.menu.setUp': 'إعداد',
        'shell.menu.loggingOut': 'جارٍ تسجيل الخروج…',
        'shell.menu.logoutFailed': 'تعذّر تسجيل الخروج. يُرجى المحاولة مرة أخرى.',
        'shell.sidebar.collapse': 'طيّ الشريط الجانبي',
        'shell.sidebar.expand': 'توسيع الشريط الجانبي',

        // ── Dashboard ────────────────────────────────────────────────────
        'dash.export': 'تصدير',
        'dash.bookingsRevenue.title': 'الحجوزات والإيرادات',
        'dash.appointmentStats.title': 'إحصاءات المواعيد',
        'dash.revenue.title': 'إحصاءات الإيرادات',
        'dash.revenue.series': 'الإيرادات',
        'dash.payments.recentTitle': 'أحدث المدفوعات',
        'dash.payments.recentEmpty': 'لا توجد مدفوعات حديثة',
        'dash.payments.appointmentRef': 'موعد رقم {id}',
        'dash.payments.dateVia': '{date} عبر {method}',
        'dash.payments.byMethodTitle': 'الإيرادات حسب طريقة الدفع',
        'dash.payments.byMethodEmpty': 'لا توجد بيانات دفع بعد',
        'dash.payments.txnCount': 'عدد الدفعات: {count}',
        'dash.payments.status.paid': 'مدفوع',
        'dash.payments.status.pending': 'قيد الانتظار',
        'dash.payments.status.failed': 'فشل',
        'dash.payments.status.refunded': 'مُسترد',
        'dash.payments.method.cash': 'نقدًا',
        'dash.payments.method.card': 'بطاقة',
        'dash.payments.method.online': 'دفع إلكتروني',

        // ── Calendar page ────────────────────────────────────────────────
        'cal.title': 'التقويم',
        'cal.viewMode': 'طريقة عرض التقويم',
        'cal.view.day': 'يوم',
        'cal.view.week': 'أسبوع',
        'cal.prevDay': 'اليوم السابق',
        'cal.nextDay': 'اليوم التالي',
        'cal.prevWeek': 'الأسبوع السابق',
        'cal.nextWeek': 'الأسبوع التالي',
        'cal.today': 'اليوم',
        'cal.pickDate': 'اختر تاريخًا',
        'cal.selectBranch': 'اختر الفرع',
        'cal.hq': 'الرئيسي',
        'cal.quickBook': '+ حجز سريع',
        'cal.quickBookWith': 'حجز سريع مع {name} +',
        'cal.quickBookNeedsBranch': 'اختر فرعًا أولًا.',
        'cal.loadFailed': 'تعذّر تحميل التقويم.',
        'cal.retry': 'إعادة المحاولة',
        'cal.closed.title': 'مغلق في هذا اليوم',
        'cal.closed.body': 'هذا الفرع مغلق يوم {date}. يمكنك تعديل ساعات العمل من «منشأتي» ← «تفاصيل المنشأة» ← «ساعات العمل».',
        'cal.toast.nobodyOnShift': 'لا يوجد موظف مناوب قرابة {time} يوم {date}. يمكنك اختيار وقت أو موظف آخر.',
        'cal.staff.openActions': 'إجراءات {name}',
        'cal.staff.actions': 'إجراءات {name}',
        'cal.staff.female': 'موظفة',
        'cal.staff.male': 'موظف',
        'cal.staff.off': 'عطلة',
        'cal.staff.clickToBook': 'انقر على وقت فارغ للحجز مع {name}',

        // ── Quick book modal ─────────────────────────────────────────────
        'cal.qb.dialog': 'حجز موعد سريع',
        'cal.qb.title.quickBook': 'حجز سريع',
        'cal.qb.title.bookWith': 'حجز مع {name}',
        'cal.qb.title.bookAt': 'حجز مع {name} الساعة {time}',
        'cal.qb.title.pickTime': 'اختر وقتًا لخدمة {service}',
        'cal.qb.atBranch': 'في {branch}',
        'cal.qb.close': 'إغلاق',
        'cal.qb.phase.staff': 'الموظف',
        'cal.qb.phase.service': 'الخدمة',
        'cal.qb.phase.slot': 'الوقت',
        'cal.qb.phase.confirm': 'التأكيد',
        'cal.qb.stepOf': 'الخطوة {n} من {total} — {label}',
        'cal.qb.step.category': 'اختر الفئة',
        'cal.qb.step.service': 'اختر الخدمة',
        'cal.qb.step.slot': 'اختر وقتًا لخدمة {service}',
        'cal.qb.step.confirm': 'تأكيد الحجز',
        'cal.qb.loadingCategories': 'جارٍ تحميل الفئات…',
        'cal.qb.loadingServices': 'جارٍ تحميل الخدمات…',
        'cal.qb.noCategories': 'لا توجد فئات نشطة بعد. أضف فئة من «منشأتي» ← «الفئات» أولًا.',
        'cal.qb.noServices': 'لا توجد خدمات نشطة في هذه الفئة. أضف خدمة من «منشأتي» ← «الخدمات».',
        'cal.qb.staffNoServices': 'لا يقدّم {name} أي خدمة في هذه الفئة.',
        'cal.qb.thisStaff': 'هذا الموظف',
        'cal.qb.service': 'الخدمة',
        'cal.qb.serviceTypeLabel': 'مخصّصة لـ',
        'cal.qb.serviceType.men': 'الرجال فقط',
        'cal.qb.serviceType.women': 'النساء فقط',
        'cal.qb.serviceType.everyone': 'الجميع',
        'cal.qb.when': 'التاريخ',
        'cal.qb.tomorrow': 'غدًا',
        'cal.qb.pickDate': 'اختر تاريخًا محددًا',
        'cal.qb.time': 'الوقت',
        'cal.qb.any': 'أي وقت',
        'cal.qb.morning': 'صباحًا',
        'cal.qb.afternoon': 'بعد الظهر',
        'cal.qb.evening': 'مساءً',
        'cal.qb.specific': 'وقت محدد',
        'cal.qb.specificTime': 'وقت محدد',
        'cal.qb.staff': 'الموظف',
        'cal.qb.female': 'أنثى',
        'cal.qb.male': 'ذكر',
        'cal.qb.bothGenders': 'يخدم الصالون الرجال والنساء',
        'cal.qb.scope.staff': 'الأوقات المتاحة لدى {name} {when} يوم {date}، ضمن مناوبته ومدة الخدمة',
        'cal.qb.scope.all': 'الأوقات المتاحة {when} يوم {date}، ضمن ساعات الفرع ومناوبات الموظفين والحجوزات الحالية',
        'cal.qb.scope.around': 'قرابة {time}',
        'cal.qb.scope.anyTime': 'في أي وقت',
        'cal.qb.slotsFailed': 'تعذّر تحميل الأوقات المتاحة. يُرجى المحاولة مرة أخرى.',
        'cal.qb.findingSlots': 'جارٍ البحث عن الأوقات المتاحة…',
        'cal.qb.noSlotsStaff': 'لا توجد أوقات متاحة لدى {name} في هذا اليوم. جرّب تاريخًا آخر.',
        'cal.qb.noSlots': 'لا توجد أوقات متاحة تطابق اختياراتك. جرّب تاريخًا آخر.',
        'cal.qb.closestMatch': 'لا يوجد وقت يطابق {time} تمامًا. نعرض أقرب الأوقات.',
        'cal.qb.availableSlots': 'الأوقات المتاحة',
        'cal.qb.match': 'الأقرب',
        'cal.qb.duration': 'المدة',
        'cal.qb.unit.min': 'دقيقة',
        'cal.qb.unit.hr': 'ساعة',
        'cal.qb.with': 'مع',
        'cal.qb.date': 'التاريخ',
        'cal.qb.customerName': 'اسم العميل (اختياري)',
        'cal.qb.walkIn': 'عميل بدون موعد',
        'cal.qb.phone': 'الهاتف (اختياري)',
        'cal.qb.cancel': 'إلغاء',
        'cal.qb.back': 'رجوع',
        'cal.qb.continue': 'متابعة إلى التأكيد',
        'cal.qb.book': 'احجز',
        'cal.qb.booking': 'جارٍ الحجز…',
        'cal.qb.toast.confirmed': 'تم تأكيد الحجز.',
        'cal.qb.toast.exists': 'هذا الحجز موجود مسبقًا.',
        'cal.qb.toast.failed': 'تعذّر إتمام الحجز. يُرجى المحاولة مرة أخرى.',

        // ── Appointments list ────────────────────────────────────────────
        'appt.tabsAria': 'طرق عرض المواعيد',
        'appt.durationMinutes': '{n} دقيقة',

        // ── Help ─────────────────────────────────────────────────────────
        'help.o.faqTitle': 'الأسئلة الشائعة',
        'help.o.needMoreTitle': 'هل تحتاج إلى مساعدة إضافية؟',
        'help.o.needMoreTickets': 'إذا لم تجد الإجابة التي تبحث عنها، يُرجى زيارة صفحة الدعم لإرسال طلب دعم. فريقنا جاهز لمساعدتك.',
        'help.o.faq.branch.q': 'كيف أضيف فرعًا جديدًا؟',
        'help.o.faq.branch.a': 'افتح «منشأتي» ← «الفروع» واختر «إضافة فرع»، ثم أدخل الاسم والعنوان ورقم الهاتف.',
        'help.o.faq.appointments.q': 'كيف أدير مواعيدي؟',
        'help.o.faq.appointments.a': 'افتح «المواعيد» أو «التقويم» من الشريط الجانبي، ثم افتح الحجز لتأكيده أو رفضه أو تسجيله كمدفوع أو عرضه على التقويم.',
        'help.o.faq.hours.q': 'كيف أعدّل ساعات العمل؟',
        'help.o.faq.hours.a': 'افتح «منشأتي» ← «تفاصيل المنشأة» وانتقل إلى «ساعات العمل»، ثم حدّد الساعات الأسبوعية واختر الفروع التي يُطبَّق عليها التغيير.',
        'help.o.faq.services.q': 'كيف أضيف الخدمات؟',
        'help.o.faq.services.a': 'افتح «منشأتي» ← «الخدمات» واختر «إضافة خدمة»، ثم أدخل الاسم والمدة والسعر والفئة.',
        'help.o.faq.contact.q': 'كيف أتواصل مع الدعم؟',
        'help.o.faq.contact.a': 'افتح صفحة الدعم من الشريط الجانبي واختر «طلب جديد» لإرسال طلب دعم. سيرد فريقنا خلال 24 ساعة.',
        'help.o.faq.password.q': 'كيف أغيّر كلمة المرور؟',
        'help.o.faq.password.a': 'افتح قائمة الحساب، واختر «إعدادات الحساب» ثم «تغيير كلمة المرور»، وأدخل كلمة المرور الحالية ثم الجديدة.',
    },
};
